import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Save, ExternalLink } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { defaultContent } from "../../content/defaultContent";
import { deepMerge } from "../../lib/merge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

const field = "w-full rounded-none border border-brand-grey bg-white px-4 py-3 text-sm focus:border-brand-green focus:outline-none";
const label = "text-xs uppercase tracking-[0.15em] text-brand-green/60 mb-2 block";

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [raw, setRaw] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: overrides } = await api.get("/content");
        const merged = deepMerge(defaultContent, overrides || {});
        setData(merged);
        setRaw(JSON.stringify(merged, null, 2));
      } catch (e) {
        setData(defaultContent);
        setRaw(JSON.stringify(defaultContent, null, 2));
      }
    })();
  }, []);

  const upd = (updater) => setData((d) => { const c = structuredClone(d); updater(c); return c; });

  const save = async (payload) => {
    setSaving(true);
    try {
      await api.put("/content", { content: payload });
      toast.success("Contenu enregistré. Les traductions seront régénérées.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const saveForm = () => save(data);

  const saveRaw = () => {
    try {
      const parsed = JSON.parse(raw);
      setData(parsed);
      save(parsed);
    } catch (e) {
      toast.error("JSON invalide : " + e.message);
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  if (!data) return <div className="flex min-h-screen items-center justify-center bg-brand-beige">Chargement…</div>;

  return (
    <div className="min-h-screen bg-brand-beige" data-testid="admin-dashboard">
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-brand-grey bg-white px-6 py-4 md:px-10">
        <div>
          <p className="font-serif text-2xl text-brand-green">Administration</p>
          <p className="text-xs text-brand-black/50">{user?.email}</p>
        </div>
        <div className="flex items-center gap-3">
          <a href="/" target="_blank" rel="noreferrer" data-testid="admin-view-site" className="flex items-center gap-2 border border-brand-green px-4 py-2 text-xs uppercase tracking-[0.12em] text-brand-green hover:bg-brand-green hover:text-white">
            <ExternalLink className="h-4 w-4" /> Voir le site
          </a>
          <button onClick={saveForm} disabled={saving} data-testid="admin-save" className="flex items-center gap-2 bg-brand-green px-5 py-2 text-xs uppercase tracking-[0.12em] text-white hover:bg-brand-black disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? "…" : "Enregistrer"}
          </button>
          <button onClick={doLogout} data-testid="admin-logout" className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-brand-black/60 hover:text-brand-black">
            <LogOut className="h-4 w-4" /> Quitter
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-6 py-10 md:px-10">
        <Tabs defaultValue="home">
          <TabsList className="mb-8 flex flex-wrap gap-2 rounded-none bg-transparent p-0">
            <TabsTrigger value="home" data-testid="admin-tab-home" className="rounded-none border border-brand-grey data-[state=active]:bg-brand-green data-[state=active]:text-white">Accueil</TabsTrigger>
            <TabsTrigger value="commodities" data-testid="admin-tab-commodities" className="rounded-none border border-brand-grey data-[state=active]:bg-brand-green data-[state=active]:text-white">Matières premières</TabsTrigger>
            <TabsTrigger value="contact" data-testid="admin-tab-contact" className="rounded-none border border-brand-grey data-[state=active]:bg-brand-green data-[state=active]:text-white">Contact</TabsTrigger>
            <TabsTrigger value="advanced" data-testid="admin-tab-advanced" className="rounded-none border border-brand-grey data-[state=active]:bg-brand-green data-[state=active]:text-white">JSON avancé</TabsTrigger>
          </TabsList>

          {/* HOME */}
          <TabsContent value="home" className="space-y-6">
            <div>
              <span className={label}>Hero — Titre</span>
              <textarea rows={2} className={field} value={data.home.hero.title} onChange={(e) => upd((c) => (c.home.hero.title = e.target.value))} data-testid="admin-hero-title" />
            </div>
            <div>
              <span className={label}>Hero — Sous-titre</span>
              <textarea rows={2} className={field} value={data.home.hero.subtitle} onChange={(e) => upd((c) => (c.home.hero.subtitle = e.target.value))} data-testid="admin-hero-subtitle" />
            </div>
            <div>
              <span className={label}>Présentation — Titre</span>
              <input className={field} value={data.home.presentation.title} onChange={(e) => upd((c) => (c.home.presentation.title = e.target.value))} />
            </div>
            <div>
              <span className={label}>Présentation — Texte</span>
              <textarea rows={4} className={field} value={data.home.presentation.body} onChange={(e) => upd((c) => (c.home.presentation.body = e.target.value))} />
            </div>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              {data.home.stats.items.map((s, i) => (
                <div key={i}>
                  <span className={label}>Chiffre {i + 1}</span>
                  <input className={`${field} mb-2`} value={s.value} onChange={(e) => upd((c) => (c.home.stats.items[i].value = e.target.value))} />
                  <input className={field} value={s.label} onChange={(e) => upd((c) => (c.home.stats.items[i].label = e.target.value))} />
                </div>
              ))}
            </div>
          </TabsContent>

          {/* COMMODITIES */}
          <TabsContent value="commodities" className="space-y-10">
            {data.commodities.map((it, i) => (
              <div key={it.slug} className="border border-brand-grey bg-white p-6">
                <p className="mb-4 font-serif text-2xl text-brand-green">{it.name}</p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div><span className={label}>Nom</span><input className={field} value={it.name} onChange={(e) => upd((c) => (c.commodities[i].name = e.target.value))} data-testid={`admin-commodity-name-${it.slug}`} /></div>
                  <div><span className={label}>Accroche</span><input className={field} value={it.tagline} onChange={(e) => upd((c) => (c.commodities[i].tagline = e.target.value))} /></div>
                </div>
                <div className="mt-4"><span className={label}>Image (URL)</span><input className={field} value={it.image} onChange={(e) => upd((c) => (c.commodities[i].image = e.target.value))} data-testid={`admin-commodity-image-${it.slug}`} /></div>
                <div className="mt-4"><span className={label}>Description</span><textarea rows={3} className={field} value={it.description} onChange={(e) => upd((c) => (c.commodities[i].description = e.target.value))} /></div>
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                  {["origin", "quality", "use", "markets", "export"].map((k) => (
                    <div key={k}><span className={label}>{k}</span><input className={field} value={it[k]} onChange={(e) => upd((c) => (c.commodities[i][k] = e.target.value))} /></div>
                  ))}
                </div>
              </div>
            ))}
          </TabsContent>

          {/* CONTACT */}
          <TabsContent value="contact" className="space-y-6">
            <div><span className={label}>Adresse</span><input className={field} value={data.contact.address} onChange={(e) => upd((c) => (c.contact.address = e.target.value))} data-testid="admin-contact-address" /></div>
            <div><span className={label}>Téléphone</span><input className={field} value={data.contact.phone} onChange={(e) => upd((c) => (c.contact.phone = e.target.value))} data-testid="admin-contact-phone" /></div>
            <div><span className={label}>E-mail</span><input className={field} value={data.contact.email} onChange={(e) => upd((c) => (c.contact.email = e.target.value))} /></div>
            <div><span className={label}>Localisation Google Maps</span><input className={field} value={data.contact.mapQuery} onChange={(e) => upd((c) => (c.contact.mapQuery = e.target.value))} /></div>
          </TabsContent>

          {/* ADVANCED */}
          <TabsContent value="advanced" className="space-y-4">
            <p className="text-sm text-brand-black/60">Édition avancée du contenu complet (JSON). Modifiez avec précaution.</p>
            <textarea data-testid="admin-raw-json" rows={22} className={`${field} font-mono text-xs`} value={raw} onChange={(e) => setRaw(e.target.value)} />
            <button onClick={saveRaw} disabled={saving} className="bg-brand-green px-6 py-3 text-xs uppercase tracking-[0.12em] text-white hover:bg-brand-black disabled:opacity-50" data-testid="admin-save-raw">
              Enregistrer le JSON
            </button>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
